import React, { createContext, useContext, useState, useEffect } from 'react';

const CRMContext = createContext();

export const useCRM = () => {
  const context = useContext(CRMContext);
  if (!context) {
    throw new Error('useCRM must be used within a CRMProvider');
  }
  return context;
};

export const CRMProvider = ({ children }) => {
  const [investors, setInvestors] = useState([]);
  const [salesLeads, setSalesLeads] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [contacts, setContacts] = useState([]);

  // Load data from localStorage on mount
  useEffect(() => {
    const savedInvestors = localStorage.getItem('crm_investors');
    const savedSalesLeads = localStorage.getItem('crm_sales_leads');
    const savedCandidates = localStorage.getItem('crm_candidates');
    const savedContacts = localStorage.getItem('crm_contacts');

    if (savedInvestors) setInvestors(JSON.parse(savedInvestors));
    if (savedSalesLeads) setSalesLeads(JSON.parse(savedSalesLeads));
    if (savedCandidates) setCandidates(JSON.parse(savedCandidates));
    if (savedContacts) setContacts(JSON.parse(savedContacts));
  }, []);

  // Save data to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('crm_investors', JSON.stringify(investors));
  }, [investors]);

  useEffect(() => {
    localStorage.setItem('crm_sales_leads', JSON.stringify(salesLeads));
  }, [salesLeads]);

  useEffect(() => {
    localStorage.setItem('crm_candidates', JSON.stringify(candidates));
  }, [candidates]);

  useEffect(() => {
    localStorage.setItem('crm_contacts', JSON.stringify(contacts));
  }, [contacts]);

  // Investor functions
  const addInvestor = (investor) => {
    const newInvestor = {
      ...investor,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setInvestors(prev => [...prev, newInvestor]);
  };

  const updateInvestor = (id, updates) => {
    setInvestors(prev => prev.map(investor => 
      investor.id === id 
        ? { ...investor, ...updates, updatedAt: new Date().toISOString() }
        : investor
    ));
  };

  const deleteInvestor = (id) => {
    setInvestors(prev => prev.filter(investor => investor.id !== id));
  };

  // Sales Lead functions
  const addSalesLead = (lead) => {
    const newLead = {
      ...lead,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setSalesLeads(prev => [...prev, newLead]);
  };

  const updateSalesLead = (id, updates) => {
    setSalesLeads(prev => prev.map(lead => 
      lead.id === id 
        ? { ...lead, ...updates, updatedAt: new Date().toISOString() }
        : lead
    ));
  };

  const deleteSalesLead = (id) => {
    setSalesLeads(prev => prev.filter(lead => lead.id !== id));
  };

  // Candidate functions
  const addCandidate = (candidate) => {
    const newCandidate = {
      ...candidate,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setCandidates(prev => [...prev, newCandidate]);
  };

  const updateCandidate = (id, updates) => {
    setCandidates(prev => prev.map(candidate => 
      candidate.id === id 
        ? { ...candidate, ...updates, updatedAt: new Date().toISOString() }
        : candidate
    ));
  };

  const deleteCandidate = (id) => {
    setCandidates(prev => prev.filter(candidate => candidate.id !== id));
  };

  // Contact functions
  const addContact = (contact) => {
    const newContact = {
      ...contact,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setContacts(prev => [...prev, newContact]);
  };

  const updateContact = (id, updates) => {
    setContacts(prev => prev.map(contact => 
      contact.id === id 
        ? { ...contact, ...updates, updatedAt: new Date().toISOString() }
        : contact
    ));
  };

  const deleteContact = (id) => {
    setContacts(prev => prev.filter(contact => contact.id !== id));
  };

  const value = {
    // Data
    investors,
    salesLeads,
    candidates,
    contacts,
    
    // Investor functions
    addInvestor,
    updateInvestor,
    deleteInvestor,
    
    // Sales Lead functions
    addSalesLead,
    updateSalesLead,
    deleteSalesLead,
    
    // Candidate functions
    addCandidate,
    updateCandidate,
    deleteCandidate,
    
    // Contact functions
    addContact,
    updateContact,
    deleteContact
  };

  return (
    <CRMContext.Provider value={value}>
      {children}
    </CRMContext.Provider>
  );
};